import { type ReactNode, Children, PropsWithChildren, useState } from 'react';

import { AnimatePresence, motion } from 'motion/react';

import { colors } from '../../colors';
import { ActionButton } from '../buttons/ActionButton/ActionButton';
import { LegoIcon, ChartLineIcon } from '../icons';
import { Heading3 } from '../Text';

import * as css from './Panel.css';

const availableIcons = {
    lego: { component: LegoIcon, width: 24, height: 24, color: colors.blue[600] },
    chart: { component: ChartLineIcon, width: 24, height: 24, color: colors.blue[600] },
};

type PanelProps = {
    /** Title displayed in the panel header. */
    title: string;

    /** Enables accordion behavior when true. */
    collapsible?: boolean;

    /** Initial open/closed state when collapsible. */
    initialState?: 'open' | 'closed';

    /** Displays the number of children next to the title. */
    countChildren?: boolean;

    /** Layout direction of the panel content. @default 'row' */
    contentDirection?: 'row' | 'column';

    /** Optional custom content rendered in the header (right side). */
    headerContent?: ReactNode;

    /** Optional icon shown before the title. */
    icon?: keyof typeof availableIcons;

    /** Optional className applied to the title element. */
    titleClassName?: string;
};

export function Panel({
    title,
    collapsible,
    initialState,
    countChildren,
    contentDirection = 'row',
    headerContent,
    icon,
    children,
    titleClassName,
}: PropsWithChildren<PanelProps>) {
    const [isOpen, setIsOpen] = useState(collapsible ? initialState === 'open' : true);

    const Icon = icon && availableIcons[icon].component;

    const toggle = () => setIsOpen((isOpen) => !isOpen);

    return (
        <div
            className={css.panel({
                type: collapsible ? 'accordion' : 'static',
            })}
        >
            <div
                className={css.header}
                role={collapsible ? 'button' : undefined}
                onClick={collapsible ? () => toggle() : undefined}
            >
                <div className={css.title}>
                    {Icon && (
                        <Icon
                            color={availableIcons[icon].color}
                            width={availableIcons[icon].width}
                            height={availableIcons[icon].height}
                        />
                    )}
                    <Heading3 className={titleClassName}>{title}</Heading3>
                    {countChildren && (
                        <div className={css.count}>
                            <Heading3 as="span">{Children.count(children)}</Heading3>
                        </div>
                    )}
                </div>
                <div className={css.headerContent}>
                    {headerContent}
                    {collapsible && (
                        <ActionButton
                            icon={isOpen ? 'collapse' : 'expand'}
                            onClick={(e) => {
                                e.stopPropagation();
                                toggle();
                            }}
                        />
                    )}
                </div>
            </div>
            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        initial="collapsed"
                        animate="open"
                        exit="collapsed"
                        style={{ overflow: 'hidden' }}
                        variants={{
                            open: { height: 'auto' },
                            collapsed: { height: 0 },
                        }}
                        transition={{
                            duration: 0.3,
                            type: 'tween',
                        }}
                    >
                        <div
                            className={css.content({
                                collapsible: Boolean(collapsible),
                                direction: contentDirection,
                            })}
                        >
                            {children}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
